import React from 'react';
import { connect } from 'react-redux';
import { Box, Grid, GridItem, Typography } from '@drone-js/core';
import { withErrorBoundary } from 'component/common/Hoc/ErrorBoundary';
import { setAttributeInTag } from 'core/utils/domUtils';
import { ActivitiesFooter } from 'component/common/Footer/ActivitiesFooter';
import { TopCitiesCarousel } from 'component/common/TopCitiesCarousel/TopCitiesCarousel';
import WhyChooseAgoda from 'component/common/WhyChooseAgoda/WhyChooseAgoda';
import { TopActivitiesDestinationContainer } from 'component/page/home/TopActivitiesDestinationContainer/TopActivitiesDestinationContainer';
import { OverlayUrlHandler } from 'app/OverlayUrlHandler';
import SearchBoxContainer from 'component/page/home/SearchBoxContainer/SearchBoxContainer';
import styled from 'styled-components';
import { trackPageVisitEvent } from 'core/messaging/analytics/analytics';
import { HomePageContextFields } from 'core/messaging/analytics/pages/pageEventsMapping';
import { StringFormatter } from '@agoda/react-handyman';
import { OrchestrateTopActivitiesInSessionStorage } from 'core-framework/util/EmptyStateSuggestion';
import { Breakpoint } from 'store/slice/screen';
import { getImageUrl } from 'core/utils/activityUrlGenerator';
import TopActivitiesAroundYou from 'component/page/home/TopActivitiesAroundYou/TopActivitiesAroundYou';
import { CartNameSpace } from 'core/constant/CartNameSpace';
import { HomeCrossSellPanel } from 'component/page/home/CrossSell/HomeCrossSellPanel';
import { HomeCrossSellFallback } from 'component/page/home/CrossSell/HomeCrossSellFallback';
import { CrossSellWrapper } from 'component/common/CrossSell/CrossSellWrapper';
import Feature from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';
import { CartFloatingContainer } from 'component/common/CartFloatingButtonContainer/CartFloatingButtonContainer';
import { OverlayType } from 'store/slice/overlay';
import { HomePageProps, mapStateToProps, mapDispatchToProps } from 'component/page/home/HomePage.selectors';
import { referrer } from 'core-framework/window/document';
import { getItemFromSessionStorage, setItemInSessionStorage } from 'core-framework/window/storage';
import { SessionStorageKey } from 'core-framework/constant/StorageKeys';
import ContinueYourBooking from 'component/page/home/ContinueYourBooking/ContinueYourBooking';

const WelcomeHeadingTypography = styled(Typography)({ textShadow: '0px 10px 20px rgb(0 0 0 / 25%)' });
const WelcomeTextTypography = styled(Typography)({ maxWidth: '270px', textAlign: 'center' });

const HomePageBgColor = 'linear-gradient(180deg, rgba(0, 0, 0, 0.5) 5.77%, rgba(255, 255, 255, 0) 46.15%)';

const HomePageComponent: React.FC<HomePageProps> = (props) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const { config, screenSize, breakpoint, isNewHomePageEnabled, isOpenSearchOverlayOnLanding } = props;
    const { cms, behavior, geo, whiteLabelConfig } = config;
    const isShowWhyChooseUs = behavior.home.showWhyChooseUs;
    const isUnifiedCrossSellCarousel = behavior.home.unifiedCrossSellCarousel;
    const countryName = geo?.countryName;
    const isSearchBoxFeatureGroup = behavior.featureExperiments.featureBehavior.SearchBoxFeatureGroup;
    const isNotMobile = !screenSize.mobile;
    const isMobile = screenSize.mobile;
    const isTablet = screenSize.tablet && !screenSize.desktop;
    const containerMarginX = isMobile ? '16' : isTablet ? '24' : '0';
    const topDestinationCityId = geo?.topDestinationCityId;
    const padding = isMobile ? '16' : isTablet ? '24' : '32';

    const [isCartResponseAvailable, setIsCartResponseAvailable] = React.useState(true);

    React.useEffect(() => {
        if (isSearchBoxFeatureGroup) {
            OrchestrateTopActivitiesInSessionStorage(config);
        }
    }, [config, isSearchBoxFeatureGroup]);

    React.useEffect(() => {
        trackPageVisitEvent<HomePageContextFields>({});

        if (!isNewHomePageEnabled) {
            setAttributeInTag(
                'html',
                'style',
                `background: ${HomePageBgColor}, url(${whiteLabelConfig.homeImageUrl}); background-repeat: no-repeat; background-size: cover; background-position: top;`,
            );
        }
        inputRef?.current?.focus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        const referrerUrl = referrer();
        const shouldShowSearchOverlay =
            config.isWebView && isOpenSearchOverlayOnLanding && !referrerUrl.includes('/activities') && !getItemFromSessionStorage(SessionStorageKey.SEARCH_OVERLAY);
        if (shouldShowSearchOverlay) {
            props.showOverlay(OverlayType.TextSearch);
            setItemInSessionStorage(SessionStorageKey.SEARCH_OVERLAY, 'true');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config.isWebView]);

    const getHomePageImageUrl = () => {
        const size: string = ['xl', 'l', 'm'].find((key: string) => breakpoint[key as keyof Breakpoint]) || 's';
        const image = `homepage-hero-${size}.jpg`;
        return getImageUrl(image);
    };

    return (
        <>
            <OverlayUrlHandler />
            <Feature.Off name={FeatureName.HomePageV2Layout}>
                <Box px="8" data-element-name="home-page" display="flex" flexDirection="column" alignItems="center" minHeight="max(90vh, 650px)">
                    <Box mt="64" display="flex" justifyContent="center" flexDirection="column" alignItems="center" width="100%">
                        <WelcomeHeadingTypography variant="heading-l" textColor="base.inverted" textAlign="center">
                            {cms[126800]}
                        </WelcomeHeadingTypography>
                        <WelcomeTextTypography variant="body-s" textColor="base.inverted" mt="8" textAlign="center">
                            {cms[130928]}
                        </WelcomeTextTypography>
                    </Box>
                    <SearchBoxContainer inputRef={inputRef} cms={cms} />
                    <TopActivitiesDestinationContainer />
                </Box>
            </Feature.Off>
            <Feature.On name={FeatureName.HomePageV2Layout}>
                <Feature.Off name={FeatureName.PageContainerSpacing}>
                    <Box data-element-name="home-page" backgroundColor="base.surface" display="flex" flexDirection="column" alignItems="center" minHeight="max(90vh, 650px)">
                        <Box position="relative" overflow="hidden" height={isMobile || isTablet ? '320px' : '375px'} width="100%">
                            <ActivityImage alt={cms[321534]} objectFit="cover" responsive="original" loading="lazy-blur" src={getHomePageImageUrl()} width="100%" height="100%" />
                            <Box
                                display="flex"
                                width="100%"
                                position="absolute"
                                height="100%"
                                bottom={0}
                                alignItems="flex-end"
                                backgroundColor={{
                                    stops: [
                                        {
                                            backgroundColor: 'base.transparent',
                                            position: '20%',
                                        },
                                        {
                                            backgroundColor: 'base.overlay.strong',
                                            position: '100%',
                                        },
                                    ],
                                }}
                            >
                                <Grid spacing="0" fluid={true}>
                                    <GridItem xl={2} lg={1} />
                                    <GridItem xl={5} lg={8} md={12} sm={12}>
                                        <Box mx={containerMarginX} height="100%" display="flex" flexDirection="column" mb={isMobile ? '32' : '48'}>
                                            <Box mb="24">
                                                <WelcomeHeadingTypography variant="heading-3xl" textColor="base.inverted" textAlign={isMobile || isTablet ? 'center' : 'start'}>
                                                    {cms[126800]}
                                                </WelcomeHeadingTypography>
                                                <Typography variant="body-s" textColor="base.inverted" mt="8" textAlign={isMobile || isTablet ? 'center' : 'start'}>
                                                    {cms[321252]}
                                                </Typography>
                                            </Box>
                                            <SearchBoxContainer inputRef={inputRef} cms={cms} isNewHomePageEnabled={isNewHomePageEnabled} />
                                        </Box>
                                    </GridItem>
                                    <GridItem xl={5} lg={3} />
                                </Grid>
                            </Box>
                        </Box>
                        <Grid spacing="0" fluid={true}>
                            <GridItem xl={2} lg={1} />
                            <GridItem xl={8} lg={10} md={12} sm={12}>
                                <Box minHeight="100%" display="flex" mx={containerMarginX}>
                                    <Box display="flex" flexDirection="column" width="100%" mt={isMobile ? '24' : '32'} mb="64">
                                        {isUnifiedCrossSellCarousel && (
                                            <CrossSellWrapper>
                                                <HomeCrossSellFallback onCartResponseFailure={() => setIsCartResponseAvailable(false)} namespace={CartNameSpace.ActivitiesHome} />
                                                {isCartResponseAvailable && <HomeCrossSellPanel namespace={CartNameSpace.ActivitiesHome} />}
                                            </CrossSellWrapper>
                                        )}
                                        <TopActivitiesAroundYou cityId={topDestinationCityId} />
                                        <TopCitiesCarousel
                                            variant="compact"
                                            title={countryName && StringFormatter.format(cms[310627], countryName)}
                                            isNewHomePageEnabled={isNewHomePageEnabled}
                                        />
                                        {isShowWhyChooseUs && (
                                            <Box display="flex" mt="48" width="100%" backgroundColor="base.surface" overflow="hidden">
                                                <WhyChooseAgoda isNewHomePageEnabled={isNewHomePageEnabled} isCompact={true} />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </GridItem>
                            <GridItem xl={2} lg={1} />
                        </Grid>
                    </Box>
                </Feature.Off>
                <Feature.On name={FeatureName.PageContainerSpacing}>
                    <Box data-element-name="home-page" backgroundColor="base.surface" display="flex" flexDirection="column" alignItems="center" minHeight="max(90vh, 650px)">
                        <Box position="relative" overflow="hidden" height={isMobile || isTablet ? '320px' : '375px'} width="100%">
                            <ActivityImage alt={cms[321534]} objectFit="cover" responsive="original" loading="lazy-blur" src={getHomePageImageUrl()} width="100%" height="100%" />
                            <Box
                                display="flex"
                                width="100%"
                                position="absolute"
                                height="100%"
                                bottom={0}
                                alignItems="flex-end"
                                backgroundColor={{
                                    stops: [
                                        {
                                            backgroundColor: 'base.transparent',
                                            position: '20%',
                                        },
                                        {
                                            backgroundColor: 'base.overlay.strong',
                                            position: '100%',
                                        },
                                    ],
                                }}
                            >
                                <Grid spacing="0" fluid={true}>
                                    <GridItem xl={1} />
                                    <GridItem xl={10} lg={12} md={12} sm={12}>
                                        <Box minHeight="100%" display="flex" px={breakpoint.xl ? '0' : padding}>
                                            <Box
                                                display="flex"
                                                flexDirection="column"
                                                width="100%"
                                                m={breakpoint.xl ? 'auto' : '0'}
                                                mb={breakpoint.xl || breakpoint.l ? '64' : '32'}
                                            >
                                                <Box width={breakpoint.xl || breakpoint.l ? '700px' : '100%'}>
                                                    <Box mb="24">
                                                        <WelcomeHeadingTypography
                                                            variant="heading-3xl"
                                                            textColor="base.inverted"
                                                            textAlign={isMobile || isTablet ? 'center' : 'start'}
                                                        >
                                                            {cms[126800]}
                                                        </WelcomeHeadingTypography>
                                                        <Typography variant="body-s" textColor="base.inverted" mt="8" textAlign={isMobile || isTablet ? 'center' : 'start'}>
                                                            {cms[321252]}
                                                        </Typography>
                                                    </Box>
                                                    <SearchBoxContainer inputRef={inputRef} cms={cms} isNewHomePageEnabled={isNewHomePageEnabled} />
                                                </Box>
                                            </Box>
                                        </Box>
                                    </GridItem>
                                    <GridItem xl={1} />
                                </Grid>
                            </Box>
                        </Box>
                        <Grid spacing="0" fluid={true}>
                            <GridItem xl={1} />
                            <GridItem xl={10} lg={12} md={12} sm={12}>
                                <Box minHeight="100%" display="flex" maxWidth="100%" m={breakpoint.xl ? 'auto' : '0'} px={breakpoint.xl ? '0' : padding}>
                                    <Box display="flex" flexDirection="column" width="100%" mt={isMobile ? '24' : '32'} mb="64">
                                        <Feature.On name={FeatureName.ContinueYourBooking}>
                                            <ContinueYourBooking />
                                        </Feature.On>
                                        {isUnifiedCrossSellCarousel && (
                                            <CrossSellWrapper>
                                                <HomeCrossSellFallback onCartResponseFailure={() => setIsCartResponseAvailable(false)} namespace={CartNameSpace.ActivitiesHome} />
                                                {isCartResponseAvailable && <HomeCrossSellPanel namespace={CartNameSpace.ActivitiesHome} />}
                                            </CrossSellWrapper>
                                        )}
                                        <TopActivitiesAroundYou cityId={topDestinationCityId} />
                                        <TopCitiesCarousel
                                            variant="compact"
                                            title={countryName && StringFormatter.format(cms[310627], countryName)}
                                            isNewHomePageEnabled={isNewHomePageEnabled}
                                        />
                                        {isShowWhyChooseUs && (
                                            <Box display="flex" mt="48" width="100%" backgroundColor="base.surface" overflow="hidden">
                                                <WhyChooseAgoda isNewHomePageEnabled={isNewHomePageEnabled} isCompact={true} />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </GridItem>
                            <GridItem xl={1} />
                        </Grid>
                    </Box>
                </Feature.On>
            </Feature.On>
            {isNotMobile && <CartFloatingContainer />}
            <ActivitiesFooter />
        </>
    );
};

HomePageComponent.displayName = 'HomePageComponent';

const HomePage = withErrorBoundary(HomePageComponent);
HomePage.displayName = 'HomePage';

export default connect(mapStateToProps, mapDispatchToProps)(HomePage);
